import type { ReviewItem } from "../../api/types";

/**
 * xmg-coafast: the review screen's reading of `suggested_option`.
 *
 * A producer sets it explicitly, on the server, to one of the item's own `options` -- never to a cancel
 * answer, and never for a genuine choice. The screen only reads it: it never invents a suggestion, and it
 * never decides which item "needs a choice" differently from the server's `accept-suggestions` endpoint
 * (an open item with options and no suggestion is skipped there and listed here under "Needs your choice").
 */

export function isOpen(item: ReviewItem): boolean {
  return item.resolved_at === null || item.resolved_at === undefined;
}

/** The suggestion, only while it is a real, still-offered answer to an open item. */
export function suggestionOf(item: ReviewItem): string | null {
  const suggested = item.suggested_option;
  if (!isOpen(item) || suggested === null || suggested === undefined) {
    return null;
  }
  return (item.options ?? []).includes(suggested) ? suggested : null;
}

/** An open item the operator must answer for real: it offers options and suggests none. */
export function needsChoice(item: ReviewItem): boolean {
  return isOpen(item) && suggestionOf(item) === null && (item.options ?? []).length > 0;
}

/**
 * An open, non-blocking item that offers no option at all (zero-value lines, repeating invoices, quotes,
 * tracked items, projects, ...): nothing to choose and nothing to accept. It is shown as a note. It never
 * gated sign-off (only BLOCK items do), so showing it as a note changes no gating. An option-less BLOCK is
 * deliberately NOT informational: it blocks, so it stays a visible row.
 */
export function isInformational(item: ReviewItem): boolean {
  return isOpen(item) && item.severity !== "BLOCK" && (item.options ?? []).length === 0;
}

/** The count behind "Accept all suggestions (N)": the server resolves exactly these. */
export function suggestionCount(items: readonly ReviewItem[]): number {
  return items.filter((item) => suggestionOf(item) !== null).length;
}

/** The message's first sentence (or first line), for an item whose producer wrote no `summary`. */
function firstSentence(message: string): string {
  const text = message.trim();
  const firstLine = text.split(/\r?\n/)[0] ?? text;
  const match = /^(.+?[.!?])(?:\s|$)/.exec(firstLine);
  return (match?.[1] ?? firstLine).trim();
}

/**
 * One plain line for a row: the server's `summary` when it has one, else the message's first sentence (an
 * item whose producer wrote no summary still gets a collapsed row). The full message is shown under
 * "Details" whenever the one-liner is shorter than it.
 */
export function oneLineSummary(item: Pick<ReviewItem, "summary" | "message">): string {
  if (item.summary !== null && item.summary !== undefined && item.summary !== "") {
    return item.summary;
  }
  return firstSentence(item.message);
}

/** Whether the row needs a "Details" disclosure: the one-liner is not the whole message. */
export function hasDetails(item: Pick<ReviewItem, "summary" | "message">): boolean {
  return oneLineSummary(item) !== item.message.trim();
}
